import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import advertisersService from '@/services/advertisers.service';
import {
  QuickStatsQuery,
  QuickStatsApiResponse,
  ReportsDataQuery,
  ReportsDataResponse,
  TrendsQuery,
  TrendsResponse,
} from '@/types/advertisers.types';

/**
 * Query Keys for React Query caching
 * Organized by feature for easy invalidation
 */
export const advertisersKeys = {
  all: ['advertisers'] as const,
  quickStats: (params?: QuickStatsQuery) => ['advertisers', 'quick-stats', params] as const,
  reportsData: (params: ReportsDataQuery) => ['advertisers', 'reports-data', params] as const,
  adsCreatedTrends: (params?: TrendsQuery) => ['advertisers', 'ads-created-trends', params] as const,
  campaignsCreatedTrends: (params?: TrendsQuery) => ['advertisers', 'campaigns-created-trends', params] as const,
};

/**
 * Hook: useQuickStats
 * Fetches dashboard quick stats with automatic caching and deduplication
 *
 * @param params - Optional query parameters (fromDate, toDate)
 * @param options - React Query options for customization
 */
export const useQuickStats = (
  params?: QuickStatsQuery,
  options?: Omit<UseQueryOptions<QuickStatsApiResponse>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: advertisersKeys.quickStats(params),
    queryFn: () => advertisersService.getQuickStats(params),
    ...options,
  });
};

/**
 * Hook: useReportsData
 * Fetches reports data for charts and tables
 * KEY: This hook prevents duplicate calls by caching results based on params
 *
 * @param params - Required query parameters (fromDate, toDate)
 * @param options - React Query options for customization
 */
export const useReportsData = (
  params: ReportsDataQuery,
  options?: Omit<UseQueryOptions<ReportsDataResponse>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: advertisersKeys.reportsData(params),
    queryFn: () => advertisersService.getReportsData(params),
    // Only fetch if we have required params
    enabled: Boolean(params.fromDate && params.toDate) && (options?.enabled !== false),
    ...options,
  });
};

/**
 * Hook: useAdsCreatedTrends
 * Fetches ads created trends data
 *
 * @param params - Optional query parameters (fromDate, toDate, advertiser)
 * @param options - React Query options for customization
 */
export const useAdsCreatedTrends = (
  params?: TrendsQuery,
  options?: Omit<UseQueryOptions<TrendsResponse>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: advertisersKeys.adsCreatedTrends(params),
    queryFn: () => advertisersService.getAdsCreatedTrends(params),
    ...options,
  });
};

/**
 * Hook: useCampaignsCreatedTrends
 * Fetches campaigns created trends data
 *
 * @param params - Optional query parameters (fromDate, toDate, advertiser)
 * @param options - React Query options for customization
 */
export const useCampaignsCreatedTrends = (
  params?: TrendsQuery,
  options?: Omit<UseQueryOptions<TrendsResponse>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: advertisersKeys.campaignsCreatedTrends(params),
    queryFn: () => advertisersService.getCampaignsCreatedTrends(params),
    ...options,
  });
};

/**
 * Hook: useAllAdvertiserData
 * Composite hook that fetches all advertiser data needed for the dashboard
 * This ensures all data is fetched in parallel but deduplicated if another component
 * is already fetching the same data
 *
 * @param params - Query parameters for all endpoints
 */
export const useAllAdvertiserData = (params: {
  quickStats?: QuickStatsQuery;
  reportsData: ReportsDataQuery;
  trends?: TrendsQuery;
}) => {
  const quickStats = useQuickStats(params.quickStats);
  const reportsData = useReportsData(params.reportsData);
  const adsCreatedTrends = useAdsCreatedTrends(params.trends);
  const campaignsCreatedTrends = useCampaignsCreatedTrends(params.trends);

  return {
    quickStats,
    reportsData,
    adsCreatedTrends,
    campaignsCreatedTrends,
    // Aggregate loading and error states
    isLoading:
      quickStats.isLoading ||
      reportsData.isLoading ||
      adsCreatedTrends.isLoading ||
      campaignsCreatedTrends.isLoading,
    isError:
      quickStats.isError ||
      reportsData.isError ||
      adsCreatedTrends.isError ||
      campaignsCreatedTrends.isError,
    error:
      quickStats.error ||
      reportsData.error ||
      adsCreatedTrends.error ||
      campaignsCreatedTrends.error,
  };
};
